import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  InputNumber,
  Radio,
  Space,
  Typography,
  message,
} from 'antd';
import {
  AppstoreOutlined,
  ColumnWidthOutlined,
  DesktopOutlined,
  DownloadOutlined,
  MobileOutlined,
  SettingOutlined,
  TabletOutlined,
} from '@ant-design/icons';
import { toPng } from 'html-to-image';
import {
  DEVICE_PRESETS,
  DEFAULT_PRESET_ID,
  MULTI_VIEW_PRESETS,
} from '../constants';
import LandingPreviewFrame from './LandingPreviewFrame';

const { Text } = Typography;

const MULTI_VIEW_ID = 'multi';

const PRESET_ICONS = {
  [MULTI_VIEW_ID]: AppstoreOutlined,
  iphone: MobileOutlined,
  tablet: TabletOutlined,
  desktop: DesktopOutlined,
  wide: ColumnWidthOutlined,
  custom: SettingOutlined,
};

function PresetLabel({ id, label }) {
  const Icon = PRESET_ICONS[id];
  return (
    <span className="landing-page-simulator__preset-label">
      {Icon ? <Icon className="landing-page-simulator__preset-icon" /> : null}
      {label}
    </span>
  );
}

PresetLabel.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

const configShape = PropTypes.shape({
  backgroundImageUrl: PropTypes.string,
  overlay: PropTypes.oneOf(['text', 'image']).isRequired,
  text: PropTypes.string,
  overlayImageUrl: PropTypes.string,
  logoColor: PropTypes.string.isRequired,
  showAnnouncementBanner: PropTypes.bool,
  primaryButton: PropTypes.shape({
    enabled: PropTypes.bool,
    label: PropTypes.string,
  }).isRequired,
  secondaryButton: PropTypes.shape({
    enabled: PropTypes.bool,
    label: PropTypes.string,
  }).isRequired,
});

const ScaledDeviceFrame = forwardRef(({
  width,
  height,
  config,
  className,
}, ref) => {
  const [scale, setScale] = useState(1);
  const hostRef = useRef(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;

    const updateScale = () => {
      const availableWidth = host.clientWidth;
      const availableHeight = host.clientHeight;
      if (!availableWidth || !availableHeight || !width || !height) return;
      const nextScale = Math.min(
        availableWidth / width,
        availableHeight / height,
        1,
      );
      setScale(nextScale);
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(host);
    return () => observer.disconnect();
  }, [width, height]);

  const scaledWidth = width * scale;
  const scaledHeight = height * scale;

  return (
    <div ref={hostRef} className={className || 'landing-page-simulator__scale-host'}>
      <div
        className="landing-page-simulator__scale-slot"
        style={{
          width: scaledWidth,
          height: scaledHeight,
        }}
      >
        <div
          className="landing-page-simulator__device-frame"
          style={{
            width,
            height,
            transform: `scale(${scale})`,
          }}
        >
          <LandingPreviewFrame
            ref={ref}
            width={width}
            height={height}
            config={config}
          />
        </div>
      </div>
    </div>
  );
});

ScaledDeviceFrame.displayName = 'ScaledDeviceFrame';

ScaledDeviceFrame.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  config: configShape.isRequired,
  className: PropTypes.string,
};

ScaledDeviceFrame.defaultProps = {
  className: undefined,
};

function MultiViewPane({ preset, config }) {
  return (
    <div className={`landing-page-simulator__multi-pane landing-page-simulator__multi-pane--${preset.id}`}>
      <Text type="secondary" className="landing-page-simulator__multi-caption">
        {preset.label}
        {' · '}
        {preset.width}
        ×
        {preset.height}
      </Text>
      <ScaledDeviceFrame
        width={preset.width}
        height={preset.height}
        config={config}
        className="landing-page-simulator__multi-scale-host"
      />
    </div>
  );
}

MultiViewPane.propTypes = {
  preset: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  config: configShape.isRequired,
};

function DeviceStage({ config }) {
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [customWidth, setCustomWidth] = useState(390);
  const [customHeight, setCustomHeight] = useState(844);
  const [exporting, setExporting] = useState(false);
  const previewFrameRef = useRef(null);

  const isMultiView = presetId === MULTI_VIEW_ID;

  const activePreset = useMemo(
    () => DEVICE_PRESETS.find((preset) => preset.id === presetId) || DEVICE_PRESETS[0],
    [presetId],
  );

  const deviceWidth = presetId === 'custom' ? customWidth : activePreset.width;
  const deviceHeight = presetId === 'custom' ? customHeight : activePreset.height;

  const multiPresets = useMemo(() => {
    const byId = Object.fromEntries(
      MULTI_VIEW_PRESETS.map((preset) => [preset.id, preset]),
    );
    return {
      iphone: byId.iphone,
      desktop: byId.desktop,
      wide: byId.wide,
    };
  }, []);

  const handleExport = useCallback(async () => {
    if (isMultiView) return;

    const node = previewFrameRef.current?.getCaptureNode?.();
    if (!node) {
      message.error('Preview is not ready to export yet.');
      return;
    }

    setExporting(true);
    try {
      const dataUrl = await toPng(node, {
        width: deviceWidth,
        height: deviceHeight,
        cacheBust: true,
      });
      const link = document.createElement('a');
      link.download = `landing-page-${presetId}-${deviceWidth}x${deviceHeight}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      message.error('Failed to export preview.');
    } finally {
      setExporting(false);
    }
  }, [deviceHeight, deviceWidth, isMultiView, presetId]);

  const radioOptions = [
    {
      label: <PresetLabel id={MULTI_VIEW_ID} label="Multi View" />,
      value: MULTI_VIEW_ID,
    },
    ...DEVICE_PRESETS.map((preset) => ({
      label: <PresetLabel id={preset.id} label={preset.label} />,
      value: preset.id,
    })),
  ];

  return (
    <div className="landing-page-simulator__stage">
      <div className="landing-page-simulator__presets">
        <Radio.Group
          value={presetId}
          onChange={(event) => setPresetId(event.target.value)}
          optionType="button"
          buttonStyle="solid"
          options={radioOptions}
        />
        {presetId === 'custom' && (
          <Space size="middle" className="landing-page-simulator__custom-size">
            <span>
              <Text type="secondary">W </Text>
              <InputNumber
                min={280}
                max={2560}
                value={customWidth}
                onChange={(value) => setCustomWidth(value || 390)}
              />
            </span>
            <span>
              <Text type="secondary">H </Text>
              <InputNumber
                min={480}
                max={1600}
                value={customHeight}
                onChange={(value) => setCustomHeight(value || 844)}
              />
            </span>
          </Space>
        )}
        <div className="landing-page-simulator__presets-end">
          {!isMultiView && (
            <Text type="secondary" className="landing-page-simulator__size-label">
              {deviceWidth}
              ×
              {deviceHeight}
            </Text>
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={exporting}
            disabled={exporting || isMultiView}
            title={isMultiView ? 'Switch to a single device to export' : undefined}
          >
            Export
          </Button>
        </div>
      </div>

      {isMultiView ? (
        <div className="landing-page-simulator__multi">
          <div className="landing-page-simulator__multi-col landing-page-simulator__multi-col--phone">
            <MultiViewPane preset={multiPresets.iphone} config={config} />
          </div>
          <div className="landing-page-simulator__multi-col landing-page-simulator__multi-col--stack">
            <MultiViewPane preset={multiPresets.desktop} config={config} />
            <MultiViewPane preset={multiPresets.wide} config={config} />
          </div>
        </div>
      ) : (
        <ScaledDeviceFrame
          ref={previewFrameRef}
          width={deviceWidth}
          height={deviceHeight}
          config={config}
        />
      )}
    </div>
  );
}

DeviceStage.propTypes = {
  config: configShape.isRequired,
};

export default DeviceStage;
